import H6 from '@base/H6/H6';
import { Spacings, mediaQuery, breakpoints, Fonts } from '@styles/index';
import PracticeLightening from '@icon/PracticeLightening';
import { useSelectorToStore } from '@hooks/reduxHooks';
import {
  getTotalPracticeDPPCount,
  getTotalPracticeDPPQuestionCount
} from '@selectors/courses';
import styled from '@emotion/styled';
import { useRouter } from 'next/router';
import P2 from '@base/P2/P2';
import P1 from '@base/P1/P1';
import Tooltip from '@base/Tooltip/Tooltip';
import { useEffect, useRef, useState } from 'react';
import { getIsEducator } from '@selectors/auth';
import { noOp } from '@constants/empty';
import { pluralize } from '@utils/textHelper';
import { localStorageAvailable } from '@utils/localStorage';
import { removeKeyFromCookie } from '@educator/Container/DashBoard/utils';

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  grid-column: span 6;
  margin-top: ${Spacings.SPACING_8B};
  margin-right: ${Spacings.SPACING_10B};
  ${mediaQuery.uptoMobile} {
    margin-top: ${Spacings.SPACING_24B}
    grid-column: span 10;
  }
`;

const IconWrapper = styled.div`
  display: flex;
  height: ${Spacings.SPACING_12B};
  width: ${Spacings.SPACING_12B};
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  background-color: var(--color-divider);
  ${mediaQuery.uptoMobile} {
    width: ${Spacings.SPACING_10B};
    height: ${Spacings.SPACING_10B};
  }
`;

const Info = styled.div`
  margin-left: ${Spacings.SPACING_4B};
  align-self: center;
  @media only screen and (max-width: ${breakpoints.mobile}) {
    margin-left: ${Spacings.SPACING_3B};
    & > h6 {
      font-size: ${Fonts.SMALL};
    }
    & > p {
      color: var(--color-text-secondary);
    }
  }
`;

const StyledToolTip = styled(Tooltip)`
  &.MuiTooltip-popper {
    width: 227px;
    height: 74px;
    margin-bottom: ${Spacings.SPACING_6B};
  }
`;

const StyledP1 = styled(P1)`
  color: var(--color-i-white);
  font-weight: ${Fonts.SEMIBOLD};
`;

const CountInfo = styled(P2)`
  margin-top: ${Spacings.SPACING_1B};
  color: var(--color-text-secondary);
`;

const DppCount = () => {
  const router = useRouter();
  const { courseUID } = router.query;

  const totalPracticeDPPCount = useSelectorToStore(
    getTotalPracticeDPPCount(courseUID)
  );

  const totalPracticeDPPQuestionCount = useSelectorToStore(
    getTotalPracticeDPPQuestionCount(courseUID)
  );

  const [showToolTip, setShowToolTip] = useState(false);
  const hasPracticeSection = totalPracticeDPPCount > 0;
  const isEducator = useSelectorToStore(getIsEducator);

  const toolTipInit = () => {
    setShowToolTip(true);
    return setTimeout(() => setShowToolTip(false), 5000);
  };

  const timeOutId = useRef();
  useEffect(() => {
    if (isEducator) {
      return;
    }

    try {
      if (localStorageAvailable()) {
        const data = JSON.parse(
          localStorage.getItem('coursePageToolTipInfo') ?? null
        );
        if (Array.isArray(data?.courseUIDList) && data?.count >= 0) {
          if (hasPracticeSection && !data.courseUIDList.includes(courseUID)) {
            data.count += 1;
            data.courseUIDList.push(courseUID);
            localStorage.setItem('coursePageToolTipInfo', JSON.stringify(data));

            if (data.count <= 3) {
              timeOutId.current = toolTipInit();
            }
          }
        } else {
          localStorage.setItem(
            'coursePageToolTipInfo',
            JSON.stringify({
              count: hasPracticeSection ? 1 : 0,
              courseUIDList: hasPracticeSection ? [courseUID] : []
            })
          );
          if (hasPracticeSection) {
            timeOutId.current = toolTipInit();
          }
        }
      }
    } catch (err) {
      noOp();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEducator, hasPracticeSection]);

  useEffect(() => {
    removeKeyFromCookie('coursePageToolTipInfo');
    return () => {
      clearTimeout(timeOutId.current);
    };
  }, []);

  return (
    <Wrapper>
      <StyledToolTip
        padding={Spacings.SPACING_4B}
        maxWidth="330px"
        title={
          <StyledP1>
            Attempt practices curated by your educator in the course
          </StyledP1>
        }
        open={showToolTip}
        borderRadius={Spacings.SPACING_2B}
        placement="top"
      >
        <IconWrapper>
          <PracticeLightening
            size={Spacings.SPACING_12B}
            viewBox="0 0 24 24"
            color="var(--color-text-secondary)"
          />
        </IconWrapper>
      </StyledToolTip>

      <Info>
        <H6 color="var(--color-text-primary)">
          {pluralize(totalPracticeDPPCount, 'practice')}
        </H6>

        <CountInfo>
          {totalPracticeDPPQuestionCount} questions by educators
        </CountInfo>
      </Info>
    </Wrapper>
  );
};

export default DppCount;
